import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../services/api';
import { uploadFileToS3 } from '../services/upload';
import type { ImageGeneration, ImageGenerationInputUpload } from '../types';

export function useImageStudio() {
  const [items, setItems] = useState<ImageGeneration[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchItems = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await api.listImageGenerations();
      setItems(result.items);
      return result.items;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load image generations');
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void fetchItems(); }, [fetchItems]);

  const pendingCount = useMemo(() => items.filter((item) => item.status === 'queued' || item.status === 'running').length, [items]);

  useEffect(() => {
    if (!pendingCount) return;
    const interval = setInterval(() => { void fetchItems(); }, 4000);
    return () => clearInterval(interval);
  }, [pendingCount, fetchItems]);

  const uploadInputs = useCallback(async (files: File[]): Promise<ImageGenerationInputUpload[]> => {
    const uploads: ImageGenerationInputUpload[] = [];
    for (const file of files) {
      const { uploadUrl, s3Key } = await api.generateImageUploadUrl(file.name, file.type, file.size);
      await uploadFileToS3(uploadUrl, file, file.type, () => {});
      uploads.push({ s3Key, filename: file.name, contentType: file.type, previewUrl: URL.createObjectURL(file) });
    }
    return uploads;
  }, []);

  const queueGeneration = useCallback(async (payload: {
    prompt: string;
    mode: 'image_only' | 'text_plus_image';
    aspectRatio: string;
    size: string;
    count: number;
    inputImages: ImageGenerationInputUpload[];
  }) => {
    const result = await api.triggerImageGeneration({
      prompt: payload.prompt,
      mode: payload.mode,
      aspectRatio: payload.aspectRatio,
      size: payload.size,
      count: payload.count,
      inputImages: payload.inputImages.map(({ s3Key, filename, contentType }) => ({ s3Key, filename, contentType })),
    });
    await fetchItems();
    return result;
  }, [fetchItems]);

  return { items, loading, error, refetch: fetchItems, uploadInputs, queueGeneration };
}
